import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import type { DayData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { getChartYAxisMin } from "web_myplann/Portfolio/Helpers/getChartYAxisMin";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { roundNumber } from "web_myplann/Shared/Utils/numberFormatters";
import { MILLIONS_SIGN, THOUSANDS_SIGN } from "web_myplann/Utils/constants";
import type { ChartTimeFrameKey } from "web_myplann/Utils/types";

const MIN_DATE_OFFSET_MS = 14 * 60 * 60 * 1000;
const MAX_DATE_OFFSET_MS = 12 * 60 * 60 * 1000;

type Params = {
	daysData: DayData[];
	timeFrame: ChartTimeFrameKey;
	currencySign: string;
};

export const useChartSeries = ({
	daysData,
	timeFrame,
	currencySign,
}: Params) => {
	const data = useMemo(
		() =>
			daysData.filter(
				(e) => !dayjs(e.date).isBefore(dayjs().subtract(timeFrame, "month")),
			),
		[daysData, timeFrame],
	);

	const { dates, values, invested, minDate, maxDate, yMin } = useMemo(() => {
		const dates = data.map((e) => e.date);
		const values = data.map((e) => roundNumber(e.value, 0));
		const invested = data.map((e) => roundNumber(e.invested, 0));
		const yMin = getChartYAxisMin(values, invested);

		const minDate =
			dates.length > 0
				? new Date(new Date(dates[0]).getTime() - MIN_DATE_OFFSET_MS)
				: undefined;
		const maxDate =
			dates.length > 0
				? new Date(
						new Date(dates[dates.length - 1]).getTime() + MAX_DATE_OFFSET_MS,
					)
				: undefined;

		return { dates, values, invested, minDate, maxDate, yMin };
	}, [data]);

	const xValueFormatter = useCallback(
		(date: Date) =>
			timeFrame > 12
				? dayjs(date).format("D.M.YYYY")
				: dayjs(date).format("D.M."),
		[timeFrame],
	);

	const yValueFormatter = useCallback(
		(val: number) =>
			val >= 1000000
				? `${val / 1000000} ${MILLIONS_SIGN}`
				: `${val / 1000} ${THOUSANDS_SIGN}`,
		[],
	);

	const seriesValueFormatter = useCallback(
		(val: number | null) => moneyFormatter(val, currencySign),
		[currencySign],
	);

	const isEmpty =
		daysData.length === 0 ||
		(values.every((e) => e === 0) && invested.every((e) => e === 0));

	return {
		dates,
		values,
		invested,
		minDate,
		maxDate,
		yMin,
		isEmpty,
		xValueFormatter,
		yValueFormatter,
		seriesValueFormatter,
	};
};
